import React, { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

import CustomTooltip from "components/common/Tooltip";
import InlineLoader from "./InlineLoader";
import { getBillingOverview } from "services/billing";
import { CustomChip, DatePicker } from "./ui";
import {
  OverviewReport,
  formatMoney,
  formatRate,
  formatDate,
  formatSyncedTime,
  titleCase,
} from "./types";

/* -------------------------------------------------------------------------- */
/* Palette — shared with Expected Funds tiers so the whole dashboard agrees.   */
/* -------------------------------------------------------------------------- */
const C_GREEN = "#1b5e42"; // settled / active / succeeded
const C_AMBER = "#c07f16"; // pending / paused
const C_PURPLE = "#8139d0"; // projected / collections line
const C_RED = "#dc2626"; // failed
const C_GRAY = "#9ca3af"; // cancelled

/* -------------------------------------------------------------------------- */
/* Hover definitions — every money figure gets one. Outstanding, Charges→Failed*/
/* and the Failed-queue total deliberately explain how they differ (PRD accept)*/
/* -------------------------------------------------------------------------- */
const DEF = {
  collected: "Cash actually received into the operating account within the selected date range (ledger cash entries).",
  outstanding:
    "Finalized invoices still unpaid right now (customer credit is already applied when an invoice is finalized). Invoices whose charge is already in flight (ACH processing) are left out, since that money appears in Expected. A current snapshot, not limited by the date range. This is NOT the same as “Charges by status → Failed” (which counts failed charge attempts in the range, retries included) or the Failed-charges queue total below (distinct unpaid invoices currently failing).",
  expected:
    "Money we expect to collect over the next 30 days: projected upcoming charges, in flight ACH charges still processing, and Stripe transfers on the way. Projected and in flight amounts are estimates net of indicative Stripe fees. A snapshot looking ahead, not limited by the date range.",
  successRate: "Succeeded charges ÷ (succeeded + failed) charges created within the selected date range. Pending charges are excluded until they complete.",
  chargesByStatus:
    "Every charge attempt created in the selected date range, grouped by its current status. A retried invoice appears once per attempt, so counts here can exceed the number of distinct invoices.",
  failedQueue:
    "Distinct invoices whose latest charge failed and are still unpaid right now — the live collections queue, not limited by the date range. Each invoice appears once (its most recent failed attempt).",
  subsByState: "Current subscription counts and monthly recurring revenue by state. A snapshot, not limited by the date range.",
  creditsToApply: "Customer credit currently on balance and not yet applied to an invoice. A snapshot, not limited by the date range.",
  refunds: "Refunds issued within the selected date range.",
  topCustomers: "Customers ranked by invoiced value within the selected date range. The % of MRR is a current snapshot.",
};

/* ------------------------------- date helpers ----------------------------- */
const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const daysAgo = (n: number) => {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() - n);
  return d;
};
const isSameDay = (a: Date | null, b: Date | null) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const shortLabel = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

/* -------------------------------- primitives ------------------------------ */
function InfoIcon({ content }: { content: React.ReactNode }) {
  return (
    <CustomTooltip content={content} direction="top">
      <span className="ov-info" aria-label="What is this?" role="img">
        ⓘ
      </span>
    </CustomTooltip>
  );
}

function Tile({ label, value, def, tone }: { label: string; value: string; def: string; tone: string }) {
  return (
    <div className={`ov-tile ov-tile--${tone}`}>
      <div className="ov-tile-head">
        <span className="para-body-s-medium color-text-body-secondary">{label}</span>
        <InfoIcon content={def} />
      </div>
      <p className="heading-h3-semibold color-text-heading-primary ov-tile-value">{value}</p>
    </div>
  );
}

/** A proportional stacked bar. Segments = [{ value, color, label }]. */
function StackedBar({ segments }: { segments: Array<{ value: number; color: string; label: string }> }) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0);
  return (
    <div className="ov-stacked">
      {total > 0 &&
        segments.map((s) =>
          s.value > 0 ? (
            <div
              key={s.label}
              className="ov-stacked-seg"
              style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
              title={`${s.label} ${formatMoney(s.value)}`}
            />
          ) : null,
        )}
    </div>
  );
}

function WidgetHead({ title, updated, note, def }: { title: string; updated?: string; note?: string; def?: string }) {
  return (
    <div className="ov-widget-head">
      <div className="flex items-center gap-05">
        <p className="heading-h5-medium color-text-heading-primary">{title}</p>
        {def ? <InfoIcon content={def} /> : null}
      </div>
      {(updated || note) && (
        <p className="para-body-xs-regular color-text-body-secondary">
          {updated ? `Updated ${updated}` : ""}
          {updated && note ? " · " : ""}
          {note || ""}
        </p>
      )}
    </div>
  );
}

/* Days-overdue severity dots (1 / 2 / 3), matching the design. */
function OverdueDots({ days }: { days: number }) {
  const count = days > 14 ? 3 : days > 3 ? 2 : 1;
  return (
    <span className="ov-dots" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i} className="ov-dot" />
      ))}
    </span>
  );
}

/* --------------------------------- chart ---------------------------------- */
function CollectionsTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload as { date: string; amountCents: number; chargeCount: number; successRate: number };
  return (
    <div className="ov-chart-tip">
      <p className="para-body-s-semibold color-text-heading-primary">{formatDate(p.date)}</p>
      <p className="para-body-s-regular color-text-body-primary">{formatMoney(p.amountCents)} collected</p>
      <p className="para-body-xs-regular color-text-body-secondary">
        {p.chargeCount} charge{p.chargeCount === 1 ? "" : "s"} · {formatRate(p.successRate)} success
      </p>
    </div>
  );
}

/* ================================ main ==================================== */
export default function Overview() {
  const [startDate, setStartDate] = useState<Date | null>(() => daysAgo(29));
  const [endDate, setEndDate] = useState<Date | null>(() => startOfDay(new Date()));
  const [data, setData] = useState<OverviewReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!startDate || !endDate) return;
      setLoading(true);
      setError(null);
      try {
        // endDate is inclusive in the picker; send an exclusive upper bound.
        const endExclusive = startOfDay(endDate);
        endExclusive.setDate(endExclusive.getDate() + 1);
        const res = (await getBillingOverview(
          startOfDay(startDate).toISOString(),
          endExclusive.toISOString(),
        )) as OverviewReport;
        if (active) setData(res);
      } catch {
        if (active) setError("Failed to load billing overview.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [startDate, endDate]);

  const chartData = useMemo(
    () => (data?.collectionsOverTime ?? []).map((p) => ({ ...p, label: shortLabel(p.date) })),
    [data],
  );

  if (loading) return <InlineLoader />;
  if (error) return <p className="para-body-m-regular color-text-body-secondary">{error}</p>;
  if (!data) return null;

  const synced = formatSyncedTime(data.syncedAt);
  const cs = data.chargesByStatus;
  const subs = data.subscriptionsByState;
  const topPctSum = data.topCustomers.reduce((s, c) => s + c.pctOfMrr, 0);
  const topBilledMax = Math.max(1, ...data.topCustomers.map((c) => c.billedCents));

  return (
    <div className="ov">
      {/* Date range control */}
      <div className="ov-range">
        <div className="flex items-center gap-05" style={{ flexWrap: "wrap" }}>
          <div style={{ width: 175 }}>
            <DatePicker name="ov-from" placeholder="Start date" value={startDate} onChange={setStartDate} maxDate={endDate || undefined} />
          </div>
          <span className="para-body-s-regular color-text-body-secondary">to</span>
          <div style={{ width: 175 }}>
            <DatePicker
              name="ov-to"
              placeholder="End date"
              value={endDate}
              onChange={setEndDate}
              minDate={startDate || undefined}
              maxDate={startOfDay(new Date())}
            />
          </div>
          {RANGES.map((r) => {
            const isActive = isSameDay(endDate, startOfDay(new Date())) && isSameDay(startDate, daysAgo(r.days - 1));
            return (
              <CustomChip
                key={r.days}
                label={r.label}
                size="small"
                onClick={() => {
                  setStartDate(daysAgo(r.days - 1));
                  setEndDate(startOfDay(new Date()));
                }}
                variant={isActive ? "filled" : "outlined"}
                color={isActive ? "primary" : "default"}
              />
            );
          })}
        </div>
        {synced !== "—" && <span className="para-body-s-regular color-text-body-secondary">Synced {synced}</span>}
      </div>

      {/* 4 tiles */}
      <div className="ov-tiles">
        <Tile label="Collected" value={formatMoney(data.tiles.collectedCents)} def={DEF.collected} tone="green" />
        <Tile label="Outstanding" value={formatMoney(data.tiles.outstandingCents)} def={DEF.outstanding} tone="amber" />
        <Tile
          label="Expected next 30 days"
          value={formatMoney(data.tiles.expectedNext30Cents)}
          def={DEF.expected}
          tone="purple"
        />
        <Tile label="Payment success rate" value={formatRate(data.tiles.paymentSuccessRate)} def={DEF.successRate} tone="green" />
      </div>

      {/* Collections over time + Charges by status */}
      <div className="ov-grid ov-grid--2to1">
        <div className="ov-widget">
          <WidgetHead title="Collections over time" updated={synced} />
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  width={64}
                  tickFormatter={(v: number) => `$${Math.round(v / 100).toLocaleString()}`}
                />
                <Tooltip content={<CollectionsTooltip />} />
                {/* linear, not monotone — plot each day's exact value, no smoothing */}
                <Line type="linear" dataKey="amountCents" stroke={C_PURPLE} strokeWidth={2} dot={{ r: 2.5, fill: C_PURPLE }} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="para-body-xs-regular color-text-body-secondary">
            Daily charge amounts, plotted at each day&apos;s exact value — no smoothing between points. Hover a day for its
            charge count and success rate.
          </p>
        </div>

        <div className="ov-widget">
          <WidgetHead title="Charges by status" updated={synced} def={DEF.chargesByStatus} />
          <StackedBar
            segments={[
              { value: cs.succeeded.amountCents, color: C_GREEN, label: "Succeeded" },
              { value: cs.pending.amountCents, color: C_AMBER, label: "Pending" },
              { value: cs.failed.amountCents, color: C_RED, label: "Failed" },
            ]}
          />
          <div className="ov-legend">
            {[
              { label: "Succeeded", color: C_GREEN, row: cs.succeeded },
              { label: "Pending", color: C_AMBER, row: cs.pending },
              { label: "Failed", color: C_RED, row: cs.failed },
            ].map((x) => (
              <div className="ov-legend-row" key={x.label}>
                <span className="ov-swatch" style={{ backgroundColor: x.color }} />
                <span className="para-body-s-regular color-text-body-primary ov-legend-label">{x.label}</span>
                <span className="para-body-s-regular color-text-body-secondary">{x.row.count.toLocaleString()}</span>
                <span className="para-body-s-semibold color-text-heading-primary ov-legend-amt">{formatMoney(x.row.amountCents)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Failed charges — display only (Retry deferred) */}
      <div className="ov-widget">
        <WidgetHead
          title="Failed charges"
          note="current queue, not limited by the date range above"
          def={DEF.failedQueue}
        />
        {data.failedCharges.length === 0 ? (
          <p className="para-body-s-regular color-text-body-secondary">No unresolved failed charges. 🎉</p>
        ) : (
          <div className="ov-failed">
            <div className="ov-failed-row ov-failed-head">
              <span>Customer</span>
              <span className="ov-num">Amount</span>
              <span>Days overdue</span>
              <span>Reason</span>
            </div>
            {data.failedCharges.map((f) => (
              <div className="ov-failed-row" key={`${f.customerId}-${f.amountCents}-${f.daysOverdue}`}>
                <span className="para-body-s-medium color-text-heading-primary">{f.customerName || "—"}</span>
                <span className="para-body-s-semibold color-text-heading-primary ov-num">{formatMoney(f.amountCents)}</span>
                <span className="ov-overdue">
                  <OverdueDots days={f.daysOverdue} />
                  <span className="para-body-s-regular color-text-body-primary">
                    {f.daysOverdue} day{f.daysOverdue === 1 ? "" : "s"}
                  </span>
                </span>
                <span className="para-body-s-regular color-text-body-primary">{f.reason}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Subscriptions by state · Credits · Top customers */}
      <div className="ov-grid ov-grid--3">
        {/* Subscriptions by state */}
        <div className="ov-widget">
          <WidgetHead title="Subscriptions by state" updated={synced} note="current snapshot, not limited by the date range above" def={DEF.subsByState} />
          <StackedBar
            segments={[
              { value: subs.active.mrrCents, color: C_GREEN, label: "Active" },
              { value: subs.paused.mrrCents, color: C_AMBER, label: "Paused" },
              { value: subs.cancelled30d.mrrCents, color: C_GRAY, label: "Cancelled" },
            ]}
          />
          <div className="ov-legend">
            {[
              { label: "Active", color: C_GREEN, s: subs.active },
              { label: "Paused", color: C_AMBER, s: subs.paused },
              { label: "Cancelled (30d) · lost MRR", color: C_GRAY, s: subs.cancelled30d },
            ].map((x) => (
              <div className="ov-legend-row" key={x.label}>
                <span className="ov-swatch" style={{ backgroundColor: x.color }} />
                <span className="para-body-s-regular color-text-body-primary ov-legend-label">{x.label}</span>
                <span className="para-body-s-regular color-text-body-secondary">{x.s.count.toLocaleString()}</span>
                <span className="para-body-s-semibold color-text-heading-primary ov-legend-amt">{formatMoney(x.s.mrrCents)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Credits */}
        <div className="ov-widget">
          <WidgetHead title="Credits" updated={synced} />
          <div className="ov-credit-card">
            <p className="para-body-xs-medium color-text-body-secondary ov-eyebrow">TO BE APPLIED</p>
            <div className="flex items-center gap-05">
              <p className="heading-h4-semibold color-text-heading-primary">{formatMoney(data.credits.toBeAppliedCents)}</p>
              <InfoIcon content={DEF.creditsToApply} />
            </div>
            <p className="para-body-xs-regular color-text-body-secondary">
              {data.credits.unappliedCount} credit{data.credits.unappliedCount === 1 ? "" : "s"} not yet applied
              {data.credits.oldestUnappliedDays != null ? ` · oldest ${data.credits.oldestUnappliedDays} days` : ""} · current
              balance, not limited by the date range
            </p>
          </div>

          {data.credits.recentlyIssued.length > 0 && (
            <>
              <p className="para-body-xs-medium color-text-body-secondary ov-eyebrow mt-1">RECENTLY ISSUED</p>
              <div className="flex flex-col">
                {data.credits.recentlyIssued.map((c, i) => (
                  <div className="ov-credit-row" key={`${c.customerName}-${c.issuedAt}-${i}`}>
                    <div className="flex flex-col">
                      <span className="para-body-s-medium color-text-heading-primary">{c.customerName || "—"}</span>
                      <span className="para-body-xs-regular color-text-body-secondary">
                        {formatDate(c.issuedAt)} ·{" "}
                        {c.applied
                          ? `Applied${c.appliedInvoiceDueDate ? ` to ${formatDate(c.appliedInvoiceDueDate)} invoice` : ""}`
                          : "Not yet applied"}
                      </span>
                    </div>
                    <span className="para-body-s-semibold color-text-heading-primary">{formatMoney(c.amountCents)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="ov-credit-row ov-credit-refunds">
            <span className="para-body-s-regular color-text-body-secondary">Refunds issued this period</span>
            <span className="flex items-center gap-05">
              <span className="para-body-s-semibold color-text-heading-primary">{formatMoney(data.credits.refundsIssuedCents)}</span>
              <InfoIcon content={DEF.refunds} />
            </span>
          </div>
        </div>

        {/* Top customers by billed value */}
        <div className="ov-widget">
          <WidgetHead title="Top customers by billed value" updated={synced} note="current snapshot, not limited by the date range above" def={DEF.topCustomers} />
          <div className="flex flex-col gap-075">
            {data.topCustomers.map((c, i) => (
              <div className="ov-topcust" key={`${c.customerName}-${i}`}>
                <div className="ov-topcust-line">
                  <span className="para-body-s-medium color-text-heading-primary">{c.customerName || "—"}</span>
                  <span className="para-body-s-semibold color-text-heading-primary">{formatMoney(c.billedCents)}</span>
                </div>
                <div className="ov-topcust-line">
                  <span className="para-body-xs-regular color-text-body-secondary">
                    {c.planName ? titleCase(c.planName) : "—"}
                    {c.seats != null ? ` — ${c.seats} seats` : ""}
                  </span>
                  <span className="para-body-xs-regular color-text-body-secondary">{formatRate(c.pctOfMrr)} of MRR</span>
                </div>
                <div className="ov-topcust-bar">
                  <div className="ov-topcust-bar-fill" style={{ width: `${(c.billedCents / topBilledMax) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          {data.topCustomers.length > 0 && (
            <p className="para-body-xs-regular color-text-body-secondary mt-1">
              Top {data.topCustomers.length} customers = {formatRate(topPctSum)} of active MRR ({formatMoney(data.activeMrrCents)}).
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
